// Экран «База»: плоский список каталог → заметка (M3).
//
// Почему плоско (M3.5). На десктопе дерево раскрывается вглубь мышкой, и это
// работает: указатель точный, отступ в 14 px читается. На экране 390 px тот же
// приём превращается в колодец — к четвёртому уровню отступы съедают половину
// строки, а название заметки схлопывается в многоточие. Поэтому уровней ровно
// два: каталог верхнего уровня и его заметки. Всё, что лежит глубже, поднимается
// в свой корневой каталог, а пройденный подкаталог остаётся подписью в мете
// карточки — путь не теряется, но список остаётся плоским.
//
// Дерево берём из общего стора (MOBILE_CONTRACT: «Данные общие… никаких
// параллельных клиентов»): `refreshTree` — это и есть `notesApi.tree()`, только
// результат виден и экрану заметки, которому нужен индекс wiki-ссылок.
// А вот ленту тянем сами: в сторе лежит лишь счётчик непрочитанного, а карточке
// нужен список путей, чтобы зажечь зелёную точку (M3.2).

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { ApiError, notesApi } from '../api'
import { load, save } from '../lib/persist'
import type { NoteType, TreeNode } from '../protocol'
import { useAppStore } from '../store/appStore'
import { queueAction, readSyncState, subscribeSync, type SyncState } from './offline'
import './base.css'

/** Значок заметки по типу — тот же набор, что в дереве десктопа (SPEC). */
const NOTE_GLYPH: Record<NoteType, string> = {
  goal: '◎',
  project: '◆',
  reference: '▣',
  log: '▤',
  note: '○',
  message: '✉',
  task: '☐',
}

const TYPE_LABEL: Record<NoteType, string> = {
  note: 'заметка',
  goal: 'цель',
  project: 'проект',
  reference: 'справка',
  log: 'лог',
  message: 'сообщение',
  task: 'задача',
}

/** Свёрнутые каталоги — по ключу каталога. Переживает перезагрузку: человек
 *  сворачивает «архив» один раз, а не каждое утро. */
const COLLAPSED_KEY = 'mobile.base'
/** Лента нужна только ради путей с непрочитанным изменением. */
const FEED_LIMIT = 200
/** Как часто пересчитывать строку синхронизации: «5 минут назад» обязано
 *  стареть само, иначе оно врёт до следующего перехода по экранам. */
const SYNC_TICK_MS = 30_000
/** Сколько висит тост об ошибке. */
const TOAST_MS = 3200

function plural(n: number, forms: [string, string, string]): string {
  const rest = Math.abs(n) % 100
  const last = rest % 10
  if (rest > 10 && rest < 20) return forms[2]
  if (last > 1 && last < 5) return forms[1]
  if (last === 1) return forms[0]
  return forms[2]
}

/** «только что» / «5 минут назад» / «вчера» / «12 марта». */
function agoLabel(ts: number): string {
  const sec = Math.max(0, Math.floor(Date.now() / 1000 - ts))
  if (sec < 60) return 'только что'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min} ${plural(min, ['минуту', 'минуты', 'минут'])} назад`
  const hour = Math.floor(min / 60)
  if (hour < 24) return `${hour} ${plural(hour, ['час', 'часа', 'часов'])} назад`
  const day = Math.floor(hour / 24)
  if (day === 1) return 'вчера'
  if (day < 7) return `${day} ${plural(day, ['день', 'дня', 'дней'])} назад`
  return new Date(ts * 1000).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
}

/** Строка статуса синхронизации (M3.3). */
function syncLabel(sync: SyncState): string {
  if (!sync.online) {
    return sync.queued > 0
      ? `офлайн · правки в очереди (${sync.queued})`
      : 'офлайн · база читается из памяти'
  }
  if (sync.queued > 0) return `отправляю правки (${sync.queued})`
  if (sync.syncedAt === null) return 'связь есть · база ещё не загружена'
  return `синхронизировано ${agoLabel(sync.syncedAt)}`
}

interface NoteRow {
  path: string
  name: string
  type: NoteType
  updated: number
  /** подкаталог внутри группы: «беседы/2026»; пусто — заметка лежит прямо в каталоге */
  sub: string
}

interface Group {
  /** путь каталога; пусто — заметки из корня базы, у них нет заголовка */
  key: string
  title: string
  notes: NoteRow[]
}

/** Собрать все заметки поддерева в один список, запомнив пройденный путь. */
function collect(nodes: TreeNode[], sub: string, out: NoteRow[]): void {
  for (const node of nodes) {
    if (node.kind === 'note') {
      out.push({
        path: node.path,
        name: node.name.replace(/\.md$/i, ''),
        type: node.type ?? 'note',
        updated: node.updated,
        sub,
      })
      continue
    }
    collect(node.children ?? [], sub ? `${sub}/${node.name}` : node.name, out)
  }
}

/** Дерево → два уровня. Каталоги верхнего уровня в порядке сервера, заметки
 *  внутри — свежие сверху: на телефоне ищут то, что трогали сегодня. */
function flatten(tree: TreeNode[]): Group[] {
  const groups: Group[] = []
  const root: NoteRow[] = []
  for (const node of tree) {
    if (node.kind === 'note') {
      collect([node], '', root)
      continue
    }
    const notes: NoteRow[] = []
    collect(node.children ?? [], '', notes)
    notes.sort((a, b) => b.updated - a.updated)
    groups.push({ key: node.path, title: node.name, notes })
  }
  root.sort((a, b) => b.updated - a.updated)
  // Заметки из корня идут первыми и без заголовка: выдумывать им каталог
  // «Без каталога» — значит показать человеку папку, которой нет на диске.
  if (root.length > 0) groups.unshift({ key: '', title: '', notes: root })
  return groups
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

interface Props {
  onOpen(path: string): void
}

export default function BaseScreen({ onOpen }: Props) {
  const tree = useAppStore((s) => s.tree)
  const treeError = useAppStore((s) => s.treeError)
  const refreshTree = useAppStore((s) => s.refreshTree)

  // Значение из localStorage обязано проверяться на тип: load() отдаёт всё,
  // что распарсилось, а `null` или объект под этим ключом роняли бы весь
  // экран на collapsed.includes(...) — и переживали бы перезагрузку, то есть
  // панель на телефоне чинилась бы только сбросом данных сайта.
  const [collapsed, setCollapsed] = useState<string[]>(() => {
    const saved = load<unknown>(COLLAPSED_KEY, [])
    return Array.isArray(saved) ? saved.filter((item): item is string => typeof item === 'string') : []
  })
  const [unread, setUnread] = useState<Set<string>>(() => new Set())
  const [creating, setCreating] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  // Строка «синхронизировано N минут назад» обязана стареть сама, а не только
  // при следующем переходе по экранам: держим тик, который просто дёргает рендер.
  const [, setTick] = useState(0)

  // Состояние очереди читаем как внешний стор: сеть отваливается и правки
  // уезжают без нашего участия, а `offline.ts` для этого и сделан.
  const sync = useSyncExternalStore(subscribeSync, readSyncState)

  // Дерево и ленту перечитываем при каждом заходе на экран: заметку мог
  // написать агент мимо панели, и не увидеть её здесь — худшее, что может быть.
  // `markSynced` звать не нужно: offline.ts сам ловит удачное обновление дерева.
  useEffect(() => {
    let alive = true
    void refreshTree()
    notesApi
      .feed(FEED_LIMIT)
      .then((page) => {
        if (!alive) return
        setUnread(new Set(page.items.filter((item) => !item.read).map((item) => item.path)))
      })
      .catch(() => {
        // непрочитанное — украшение строки, ронять из-за него экран не за что
      })
    return () => {
      alive = false
    }
  }, [refreshTree])

  useEffect(() => {
    const timer = window.setInterval(() => setTick((n) => n + 1), SYNC_TICK_MS)
    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    if (toast === null) return
    const timer = window.setTimeout(() => setToast(null), TOAST_MS)
    return () => window.clearTimeout(timer)
  }, [toast])

  const groups = useMemo(() => flatten(tree), [tree])
  const total = useMemo(
    () => groups.reduce((sum, group) => sum + group.notes.length, 0),
    [groups],
  )

  const toggle = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
      save(COLLAPSED_KEY, next)
      return next
    })
  }, [])

  // FAB создаёт заметку сразу, без диалога с названием (M3.4): набирать имя на
  // телефоне — три лишних касания до первой мысли. Имя даём по времени,
  // переименует потом агент или десктоп.
  const create = useCallback(async () => {
    if (creating) return
    const now = new Date()
    const title =
      `Заметка ${pad(now.getDate())}.${pad(now.getMonth() + 1)}` +
      ` ${pad(now.getHours())}:${pad(now.getMinutes())}`
    // Двоеточие в имени файла ни к чему — в заголовке заметки оно останется.
    const path = `${title.replace(/:/g, '.')}.md`

    // Без сети заметку в очередь, но НЕ открываем: файла ещё нет, и читалка
    // показала бы «не удалось открыть». Честнее сказать, что она появится.
    if (!navigator.onLine) {
      queueAction({ kind: 'note', path, title })
      setToast('Нет сети — заметка создастся, как только связь вернётся.')
      return
    }

    setCreating(true)
    try {
      const note = await notesApi.create({ path, title })
      await refreshTree()
      onOpen(note.path)
    } catch (error) {
      setToast(error instanceof ApiError ? error.message : 'Не удалось создать заметку')
    } finally {
      setCreating(false)
    }
  }, [creating, onOpen, refreshTree])

  return (
    <div className="ab-mbase">
      {/* Прокрутка — по соглашению оболочки: .ab-mscroll даёт общий overscroll,
          чтобы край списка не тянул за собой страницу. */}
      <div className="ab-mscroll ab-mbase__scroll">
        <div className="ab-mbase__sync">
          <span className={`ab-mbase__syncdot${sync.online ? '' : ' is-off'}`} />
          <span className="ab-mbase__synctext">{syncLabel(sync)}</span>
        </div>

        {treeError !== null && <div className="ab-mbase__error">{treeError}</div>}

        {treeError === null && total === 0 && (
          <div className="ab-mbase__empty">
            База пуста. Нажмите «+» — заметка создастся и откроется на чтение.
          </div>
        )}

        {groups.map((group) => {
          const shut = group.key !== '' && collapsed.includes(group.key)
          return (
            // Ключ с префиксом, а не сам путь: у группы «без каталога» путь пуст,
            // и префикс даёт ей собственный ключ, не совпадающий ни с одной папкой.
            <div className="ab-mbase__group" key={`g:${group.key}`}>
              {group.key !== '' && (
                <button
                  type="button"
                  className="ab-mbase__folder"
                  onClick={() => toggle(group.key)}
                  aria-expanded={!shut}
                >
                  <span className="ab-mbase__chev">{shut ? '▸' : '▾'}</span>
                  <span className="ab-mbase__foldername">{group.title}</span>
                  <span className="ab-mbase__count">{group.notes.length}</span>
                </button>
              )}
              {!shut &&
                group.notes.map((note) => (
                  <button
                    type="button"
                    className="ab-mbase__note"
                    key={note.path}
                    onClick={() => onOpen(note.path)}
                  >
                    <span className={`ab-mbase__glyph is-${note.type}`}>
                      {NOTE_GLYPH[note.type]}
                    </span>
                    <span className="ab-mbase__text">
                      <span className="ab-mbase__title">{note.name}</span>
                      <span className="ab-mbase__meta">
                        {[TYPE_LABEL[note.type], note.sub, agoLabel(note.updated)]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                    {unread.has(note.path) && (
                      <span className="ab-mbase__unread" title="есть непрочитанное изменение" />
                    )}
                  </button>
                ))}
            </div>
          )
        })}
      </div>

      <button
        type="button"
        className="ab-mbase__fab"
        onClick={() => void create()}
        disabled={creating}
        title="новая заметка"
      >
        +
      </button>

      {toast !== null && <div className="ab-mbase__toast">{toast}</div>}
    </div>
  )
}
